/**
 * @module tracking/handIdentity
 *
 * Hand-identity gating: assigns per-frame MediaPipe detections to stable
 * slots (0 and 1) via nearest-neighbour continuity, and debounces new
 * acquisitions so single-frame phantom detections never reach the cursor.
 *
 * WHY THIS MODULE EXISTS
 * ──────────────────────
 * MediaPipe does not guarantee a stable ordering of detected hands across
 * frames: "hand[0]" in frame N may be the opposite physical hand in frame
 * N+1. At the lowered detection confidence (0.35), spurious hits — faces,
 * shoulders, reflections — can appear for a single frame and snap the
 * cursor to an unrelated screen region. Before this module, the game used
 * the raw array index to route detections to slots, which caused two
 * observable failure modes:
 *
 *   1. INDEX SWAP JUMP — when MediaPipe re-ordered the two hands between
 *      frames, slot 0 received the data for what was previously slot 1,
 *      instantly teleporting the cursor across the screen.
 *
 *   2. PHANTOM JUMP — a single-frame false-positive detection at (0.9, 0.1)
 *      was mapped to slot 0 without question, yanking the cursor to a
 *      corner for one frame (one rAF = ~16 ms) — invisible in logs, very
 *      visible to the player.
 *
 * ALGORITHM OVERVIEW
 * ──────────────────
 * Each call to assignHands() operates on the fresh set of candidates and
 * the module-level slot state:
 *
 *   CONTINUITY pass  — greedy global-nearest matching between fresh (recently
 *   seen) slots and this frame's candidates.  Preserves identity across index
 *   swaps; the match radius grows linearly with dt so fast slashes at 10fps
 *   tracking (adaptive throttle on slow devices) still match correctly.
 *
 *   ACQUISITION pass — remaining (unmatched) candidates may claim a free
 *   (not-fresh) slot, but only after acquireConfirmFrames consecutive frames
 *   where the candidate remains near the same position.  A candidate that
 *   appears for just one frame is silently discarded.  When a candidate
 *   finally confirms, resetFilter:true tells the caller to clear the
 *   one-euro filter for that slot (prevents position-jump velocity spike).
 *
 *   PENDING cleanup  — pendings not touched this frame are cleared; the
 *   confirmation counter resets, so a non-consecutive appearance restarts
 *   the debounce.
 */

import { CONFIG } from '../game-config.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** A raw detection candidate — normalised tip coordinates + source array index. */
export interface HandCandidate {
  /** Normalised [0..1] tip X coordinate (already mirrored if applicable). */
  tipX: number;
  /** Normalised [0..1] tip Y coordinate. */
  tipY: number;
  /** Index into the original landmarks array this candidate came from. */
  index: number;
}

/** The result of assigning a single candidate to a slot. */
export interface SlotAssignment {
  /** Which slot (0 or 1) this candidate has been assigned to. */
  slot: 0 | 1;
  /** The candidate's original landmarks array index (pass-through). */
  index: number;
  /**
   * When true the caller MUST reset the one-euro filter for this slot.
   * Set on first acquisition (new hand or stale slot reacquisition) to
   * prevent the stale previous position from causing a velocity spike.
   */
  resetFilter: boolean;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

interface SlotState {
  /** Normalised X of last confirmed tip position. */
  x: number;
  /** Normalised Y of last confirmed tip position. */
  y: number;
  /** Timestamp (ms) of last confirmed detection — used for stale check. */
  lastSeenMs: number;
}

interface PendingState {
  /** Candidate position centroid during accumulation. */
  x: number;
  y: number;
  /** Consecutive frames this candidate has appeared near this position. */
  frames: number;
  /** True if this pending was touched during the CURRENT assignHands() call. */
  touchedThisFrame: boolean;
}

/** -1 sentinel means "never seen" — avoids undefined optionals. */
const NEVER_SEEN = -Infinity;

const _slots: [SlotState | null, SlotState | null] = [null, null];
const _pending: [PendingState | null, PendingState | null] = [null, null];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function _dist(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return Math.sqrt(dx * dx + dy * dy);
}

function _isFresh(slot: SlotState, nowMs: number): boolean {
  return nowMs - slot.lastSeenMs <= CONFIG.tracking.identity.slotStaleMs;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Assign a fresh set of detections to stable slots.
 *
 * @param candidates  Raw detections from MediaPipe this frame (0–2 items).
 * @param nowMs       Current timestamp in milliseconds (must be >= previous call).
 * @param dtMs        Elapsed milliseconds since previous tracking frame.
 * @returns           Assignments for candidates that have been accepted this frame.
 *                    Candidates under acquisition debounce are NOT included.
 */
export function assignHands(
  candidates: readonly HandCandidate[],
  nowMs: number,
  dtMs: number,
): SlotAssignment[] {
  const cfg = CONFIG.tracking.identity;
  const matchRadius =
    cfg.maxMatchBaseNorm + cfg.maxMatchSpeedNormPerSec * (dtMs / 1000);

  const assignments: SlotAssignment[] = [];

  // Track which candidates and slots are still available after each pass.
  const remainingCandidates = new Set<number>(
    candidates.map((_, i) => i),
  );
  const assignedSlots = new Set<0 | 1>();

  // ── CONTINUITY pass ────────────────────────────────────────────────────────
  // Collect all (freshSlot, candidateIndex) pairs with their distances, then
  // greedily pick the closest pair until no more matches are possible.

  interface Pair {
    slot: 0 | 1;
    ci: number; // index into candidates array
    dist: number;
  }
  const pairs: Pair[] = [];

  for (const slotIdx of [0, 1] as const) {
    const slot = _slots[slotIdx];
    if (!slot || !_isFresh(slot, nowMs)) continue;

    for (const ci of remainingCandidates) {
      const c = candidates[ci];
      if (!c) continue;
      const d = _dist(slot.x, slot.y, c.tipX, c.tipY);
      if (d <= matchRadius) {
        pairs.push({ slot: slotIdx, ci, dist: d });
      }
    }
  }

  // Sort by distance ascending for greedy nearest-first matching.
  pairs.sort((a, b) => a.dist - b.dist);

  for (const { slot, ci, dist: _dist_ } of pairs) {
    if (assignedSlots.has(slot) || !remainingCandidates.has(ci)) continue;
    void _dist_; // used only for sorting

    const c = candidates[ci];
    if (!c) continue;

    // Accept — update slot, emit assignment.
    _slots[slot] = { x: c.tipX, y: c.tipY, lastSeenMs: nowMs };
    assignedSlots.add(slot);
    remainingCandidates.delete(ci);
    assignments.push({ slot, index: c.index, resetFilter: false });
  }

  // ── ACQUISITION pass ───────────────────────────────────────────────────────
  // Mark all pendings as not-yet-touched this frame.
  for (const slotIdx of [0, 1] as const) {
    if (_pending[slotIdx]) {
      _pending[slotIdx].touchedThisFrame = false;
    }
  }

  // For each remaining candidate, try to claim a FREE slot (unassigned AND not fresh).
  for (const ci of remainingCandidates) {
    const c = candidates[ci];
    if (!c) continue;

    // Find a free slot (not assigned this frame, not fresh, not already claimed by a
    // different candidate's pending this frame).
    for (const slotIdx of [0, 1] as const) {
      if (assignedSlots.has(slotIdx)) continue;
      const existingSlot = _slots[slotIdx];
      if (existingSlot && _isFresh(existingSlot, nowMs)) continue;

      // This slot is free. Check pending.
      const pend = _pending[slotIdx];

      // If another candidate already claimed (touched) this slot's pending this frame,
      // skip — one pending can only be advanced by one candidate per frame.
      if (pend && pend.touchedThisFrame) continue;

      if (
        pend &&
        _dist(pend.x, pend.y, c.tipX, c.tipY) <= cfg.pendingMatchDistNorm
      ) {
        // Candidate is near the existing pending — advance confirmation.
        pend.frames += 1;
        pend.x = c.tipX;
        pend.y = c.tipY;
        pend.touchedThisFrame = true;

        if (pend.frames >= cfg.acquireConfirmFrames) {
          // Confirmed — accept and clear pending.
          _slots[slotIdx] = { x: c.tipX, y: c.tipY, lastSeenMs: nowMs };
          assignedSlots.add(slotIdx);
          _pending[slotIdx] = null;
          assignments.push({ slot: slotIdx, index: c.index, resetFilter: true });
        }
        // Whether confirmed or not, this candidate is claimed by this slot's pending.
        break;
      } else {
        // New (or displaced) candidate — start / restart pending.
        _pending[slotIdx] = {
          x: c.tipX,
          y: c.tipY,
          frames: 1,
          touchedThisFrame: true,
        };
        // Candidate is claimed by this slot's pending (no assignment emitted yet).
        break;
      }
    }
  }

  // ── PENDING CLEANUP ────────────────────────────────────────────────────────
  // Pendings not touched this frame must be cleared — confirmation requires
  // consecutive frames.
  for (const slotIdx of [0, 1] as const) {
    const pend = _pending[slotIdx];
    if (pend && !pend.touchedThisFrame) {
      _pending[slotIdx] = null;
    }
  }

  return assignments;
}

/**
 * Reset all slot and pending state.
 * Call on game restart, camera reinit, or worker reconnect.
 */
export function resetHandIdentity(): void {
  _slots[0] = null;
  _slots[1] = null;
  _pending[0] = null;
  _pending[1] = null;
}

// Suppress "declared but never used" for the NEVER_SEEN sentinel.
void NEVER_SEEN;
